import React, { useState } from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import DashboardOverview from '../components/dashboard/DashboardOverview';
import ProductManagement from '../components/dashboard/ProductManagement';
import FeaturedProductsManagement from '../components/dashboard/FeaturedProductsManagement';
import OrderManagement from '../components/dashboard/OrderManagement';
import UserManagement from '../components/dashboard/UserManagement';
import SalesReport from '../components/dashboard/SalesReport';
import CustomerOrders from '../components/dashboard/CustomerOrders';
import ProfileSection from '../components/dashboard/ProfileSection';
import ChangePasswordSection from '../components/dashboard/ChangePasswordSection';

function DashboardPage({ isMobile, setCurrentPage, user, onLogout, products, featuredProducts, updateProducts, updateFeaturedProducts, fetchProducts, fetchFeaturedProducts }) {
  const [activeTab, setActiveTab] = useState('overview');

  const staffTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'products', name: 'Product Management', icon: '🍖' },
    { id: 'featured', name: 'Featured Products', icon: '⭐' },
    { id: 'orders', name: 'Order Management', icon: '📦' },
    { id: 'users', name: 'User Management', icon: '👥' },
    { id: 'sales', name: 'Sales Report', icon: '📈' },
    { id: 'profile', name: 'My Profile', icon: '👤' }
  ];

  const customerTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'orders', name: 'My Orders', icon: '📦' },
    { id: 'profile', name: 'My Profile', icon: '👤' },
    { id: 'change-password', name: 'Change Password', icon: '🔒' }
  ];

  const tabs = user?.role === 'staff' ? staffTabs : customerTabs;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview user={user} isMobile={isMobile} setCurrentPage={setCurrentPage} />;
      case 'products':
        return user?.role === 'staff' ? 
          <ProductManagement 
            products={products} 
            updateProducts={updateProducts}
            fetchProducts={fetchProducts}
            isMobile={isMobile} 
          /> : null;
      case 'featured':
        return user?.role === 'staff' ? 
          <FeaturedProductsManagement 
            products={products}
            featuredProducts={featuredProducts} 
            updateFeaturedProducts={updateFeaturedProducts}
            fetchFeaturedProducts={fetchFeaturedProducts}
            isMobile={isMobile} 
          /> : null;
      case 'orders':
        return user?.role === 'staff' ? 
          <OrderManagement isMobile={isMobile} user={user} /> : 
          <CustomerOrders isMobile={isMobile} user={user} setCurrentPage={setCurrentPage} />;
      case 'users':
        return user?.role === 'staff' ? <UserManagement isMobile={isMobile} /> : null;
      case 'sales':
        return user?.role === 'staff' ? <SalesReport isMobile={isMobile} /> : null;
      case 'profile':
        return <ProfileSection user={user} setCurrentPage={setCurrentPage} isMobile={isMobile} />;
      case 'change-password':
        return <ChangePasswordSection setCurrentPage={setCurrentPage} isMobile={isMobile} user={user} />;
      default:
        return <DashboardOverview user={user} isMobile={isMobile} setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        isMobile={isMobile} 
        currentPage="dashboard"
        setCurrentPage={setCurrentPage}
        cartItemCount={0}
        user={user}
        onLogout={onLogout}
      />
      
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {user?.role === 'staff' ? 'Staff Dashboard' : 'My Account'}
          </h1>
          <p className="text-gray-600">
            Welcome back, {user?.name || user?.email}! {user?.role === 'staff' ? 'Manage your butchery efficiently.' : 'Track your orders and manage your account.'}
          </p>
        </div>

        <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-6`}>
          <div className={`${isMobile ? 'w-full' : 'w-64'} bg-white rounded-xl shadow-md p-4`}>
            <nav className="space-y-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                Role: <strong>{user?.role === 'staff' ? 'Staff Member' : 'Customer'}</strong>
              </p>
              <button 
                className="w-full border border-yellow-500 text-yellow-600 hover:bg-yellow-50 py-2 rounded-lg transition-colors"
                onClick={() => setCurrentPage('shop')}
              >
                ← Back to Shop
              </button>
            </div>
          </div>

          <div className="flex-1 bg-white rounded-xl shadow-md p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>

      <Footer isMobile={isMobile} setCurrentPage={setCurrentPage} />
    </div>
  );
}

export default DashboardPage;